import { useCallback, useMemo } from 'react';
import {
  DataTable,
  EmptyState,
  EmptyStateDescription,
  EmptyStateTitle,
  FormattedDateTime,
  InlineStack,
  Link,
  Spinner,
  Stack,
  Text,
  useConfirmationModal,
  useSnackbars,
} from '@customerio/pluma-components/react';
import { createColumnHelper, type Row, type Table as TanstackTable } from '@tanstack/table-core';
import { useRouter } from '@tanstack/react-router';
import { usePermissions } from '@customerio/hydra-permissions/react';
import { isEmptyString } from '@/utils/string';
import { importIsInvalid, type Collection } from '@/services/db/collection/entity';
import { useDeleteCollection } from '@/services/db/collection/mutations';
import { useCollectionFormModal } from './collection-form-modal';

const columnHelper = createColumnHelper<Collection>();

type NameCellProps = {
  collection: Collection;
  workspaceId: number;
};

function NameCell({ collection, workspaceId }: NameCellProps) {
  const router = useRouter();
  const collectionDetailHref = router.buildLocation({
    to: '/workspaces/$workspaceId/journeys/collections/$collectionId',
    params: { workspaceId, collectionId: collection.id },
  }).href;

  const nameContent = collection.importing ? (
    <InlineStack alignBlock="center" gap="100">
      <Spinner />
      <Text>{collection.name}</Text>
      <Text color="subtle">Processing…</Text>
    </InlineStack>
  ) : importIsInvalid(collection) ? (
    <InlineStack alignBlock="center" gap="100">
      <Text>{collection.name}</Text>
      <Text color="critical">Failed</Text>
    </InlineStack>
  ) : (
    <Link href={collectionDetailHref}>{collection.name}</Link>
  );

  return (
    <Stack gap="050">
      {nameContent}
      {!isEmptyString(collection.description) ? <Text color="subtle">{collection.description}</Text> : null}
    </Stack>
  );
}

function createColumns(workspaceId: number) {
  return [
    columnHelper.accessor('name', {
      id: 'name',
      header: 'Name',
      enableSorting: true,
      meta: { width: '2fr', minWidth: 240 },
      cell: (info) => <NameCell collection={info.row.original} workspaceId={workspaceId} />,
    }),
    columnHelper.accessor('rows', {
      id: 'rows',
      header: 'Items',
      enableSorting: true,
      meta: { width: 120 },
      cell: (info) => info.getValue(),
    }),
    columnHelper.accessor('updated_at', {
      id: 'updated_at',
      header: 'Last Updated',
      enableSorting: true,
      meta: { width: 220 },
      cell: (info) => <FormattedDateTime date={info.getValue()} format="medium" />,
    }),
  ];
}

type CollectionsEmptyStateProps = {
  table: TanstackTable<Collection>;
};

// The DataTable renders this whenever no rows are visible; here that only
// happens when a client-side search filters everything out (the page shows a
// richer hero when the workspace has zero collections).
function CollectionsSearchEmptyState({ table }: CollectionsEmptyStateProps) {
  const globalFilter = table.getState().globalFilter;
  const search = typeof globalFilter === 'string' ? globalFilter : '';

  return (
    <EmptyState>
      <EmptyStateTitle icon="search">No collections found</EmptyStateTitle>
      <EmptyStateDescription>
        {isEmptyString(search) ? 'No collections to show.' : `No collections match "${search}".`}
      </EmptyStateDescription>
    </EmptyState>
  );
}

type CollectionsTableProps = {
  collections: readonly Collection[];
  workspaceId: number;
};

export function CollectionsTable({ collections, workspaceId }: CollectionsTableProps) {
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission('journeys/collections/edit') === true;
  const canDelete = hasPermission('journeys/collections/delete') === true;

  const collectionModal = useCollectionFormModal();
  const deleteCollection = useDeleteCollection({ workspaceId });
  const confirm = useConfirmationModal();
  const snackbars = useSnackbars();

  const columns = useMemo(() => createColumns(workspaceId), [workspaceId]);
  const data = useMemo(() => [...collections], [collections]);

  const handleDelete = useCallback(
    async (collection: Collection) => {
      const confirmed = await confirm({
        isDanger: true,
        title: 'Delete collection?',
        message: `This will permanently delete ${collection.name}. This cannot be undone.`,
        confirmLabel: 'Delete',
        cancelLabel: 'Cancel',
      });
      if (!confirmed) return;
      try {
        await deleteCollection.mutateAsync({ id: collection.id });
        snackbars.create({ variant: 'success', message: `${collection.name} deleted.` });
      } catch {
        snackbars.create({ variant: 'error', message: "Collection couldn't be deleted. Try again." });
      }
    },
    [confirm, deleteCollection, snackbars],
  );

  return (
    <DataTable
      caption="Collections"
      data={data}
      columns={columns}
      getRowId={(collection) => collection.id}
      shouldHighlightOnHover
      withSorting
      withSearch
      search={{ placeholder: 'Find by collection name', shouldSearchOnInput: true, shouldSearchOnClear: true }}
      withPagination
      emptyStateComponent={CollectionsSearchEmptyState}
      rowActions={(row: Row<Collection>) => {
        const collection = row.original;
        return {
          dropdownTrigger: { ariaLabel: `Open actions for ${collection.name}` },
          dropdownActions: [
            {
              label: 'Update collection',
              icon: 'edit',
              isDisabled: !canEdit || collection.importing,
              onClick: () => collectionModal.open({ workspaceId, collection }),
            },
            {
              label: 'Delete collection',
              icon: 'delete-trash',
              isDanger: true,
              isDisabled: !canDelete || collection.in_use || collection.importing,
              tooltip: collection.in_use ? "In use by campaigns and can't be deleted" : undefined,
              onClick: () => void handleDelete(collection),
            },
          ],
        };
      }}
    />
  );
}
